<template>
  <div>
    <div class="pl-slot-row flex align-items-center gap-sm p-y-m cursor-pointer" data-cy="availability-slot-row" @click="viewSlot">
      <div class="flex-1" style="min-width: 0;">
        <p class="m-b-none font-semi pl-slot-time">
          {{ timeRange }}
          <span class="font-normal text-gray-600">&middot; {{ durationMinutes }} min</span>
        </p>
        <p v-if="planSummary" class="m-b-none text-14 text-gray-600">{{ planSummary }}</p>
        <p v-if="wrestlerSummary" class="m-b-none text-14 text-gray-600 pl-truncate">{{ wrestlerSummary }}</p>
        <p v-else-if="!planSummary" class="m-b-none text-14 text-gray-600 pl-truncate">{{ openSummary }}</p>
      </div>
      <button
        v-if="showLessonActions && actionType === 'book'"
        type="button"
        class="btn btn-link btn-compact text-blue-600 flex-shrink-0 p-x-xs"
        data-cy="slot-book"
        @click.stop="viewSlot">
        Book
      </button>
      <!-- Trash sits left of the badge so the (non-interactive) badge buffers
           it from the chevron edge where open-the-row taps land -->
      <button
        v-if="deletable"
        type="button"
        class="btn btn-link p-x-sm p-y-sm text-gray-500 flex-shrink-0"
        :aria-label="`Delete ${timeRange} slot`"
        @click.stop="$emit('delete', lessonSlot)">
        <i class="fa-regular fa-trash-can"></i>
      </button>
      <span class="pl-slot-badge flex-shrink-0" :class="slotStatusBadgeClass(lessonSlot.status)" data-cy="slot-status-badge">{{ slotStatusLabel(lessonSlot.status) }}</span>
      <!-- The whole row navigates to the event page (cancel wrestlers, cancel
           the lesson, edit bookings) — make that discoverable -->
      <i class="fa-solid fa-chevron-right text-gray-400 flex-shrink-0" aria-hidden="true"></i>
    </div>
    <!-- Contextual lesson actions for the coach home: confirm/decline a booking
         awaiting the coach, check out a finished lesson. (Booking an open slot
         is a compact inline action on the row itself — every open slot has it,
         so a boxed sub-row button per slot was pure noise.) -->
    <div v-if="showLessonActions && (actionType === 'confirm' || actionType === 'checkout')" class="flex gap-x-xs m-b-xs" @click.stop>
      <template v-if="actionType === 'confirm'">
        <button type="button" class="btn btn-primary btn-sm" data-cy="slot-confirm" @click="$emit('update-status', { lessonSlot, status: 'confirmed' })">
          <i class="fa-solid fa-check m-r-xxs"></i> Confirm booking
        </button>
        <button type="button" class="btn btn-danger btn-outline btn-sm" data-cy="slot-decline" @click="$emit('update-status', { lessonSlot, status: 'declined' })">
          Decline
        </button>
      </template>
      <a v-else-if="actionType === 'checkout'" :href="lessonCheckoutHref(lessonSlot.privateLessonId)" class="btn btn-primary btn-sm" data-cy="slot-checkout" @click="handleLessonCheckoutClick($event, lessonSlot.privateLessonId)">
        Check out
      </a>
    </div>
    <delete-event-form
      v-if="confirmingDelete"
      :event="deleteFormEvent"
      :recurring="lessonSlot.recurring"
      variant="availability"
      class="m-b-sm"
      @click.native.stop
    ></delete-event-form>
  </div>
</template>

<script>
import DeleteEventForm from './delete_event_form.vue';
import { privateLessonStatusMix } from './mixins/private_lesson_status_mix';
import { lessonCheckoutMix } from './mixins/private_lesson_booking_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import { currencyMix } from '../../shared/currency_mix';

// One availability slot in the coach agenda: time range, status badge, who has
// it booked, and a delete affordance. Tapping the row goes to the event page
// (same behavior the calendar had). The delete confirm expands inline and is
// the existing delete-event-form, so single/future/all recurring deletes work.
export default {
  name: 'availability-slot-row',
  components: { DeleteEventForm },
  mixins: [privateLessonStatusMix, lessonCheckoutMix, appEmbedMix, currencyMix],
  props: {
    lessonSlot: {
      type: Object,
      required: true,
    },
    // Which slot id (if any) currently shows the inline delete confirm; the
    // parent owns this so only one confirm is open at a time.
    confirmingDeleteSlotId: {
      type: [Number, String],
      default: null,
    },
    // Coach-home mode: render confirm/decline, checkout, and book-this-slot
    // affordances. The parent handles the actual status update API call.
    // (forAppEmbed comes from appEmbedMix.)
    showLessonActions: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    timeRange() {
      return `${this.lessonSlot.startAt.format('h:mm')} – ${this.lessonSlot.endAt.format('h:mm a')}`;
    },
    durationMinutes() {
      return this.lessonSlot.endAt.diff(this.lessonSlot.startAt, 'minutes');
    },
    // What was booked: plan name, per-wrestler rate, and remaining capacity
    // for group lessons. Open slots have no plan (families choose one when
    // booking), so this only renders on booked/invited/completed rows.
    planSummary() {
      const plan = this.lessonSlot.billingPlan;
      if (!plan) {
        return null;
      }
      const parts = [];
      if (plan.name) {
        parts.push(plan.name);
      }
      // Capacity before rate: on narrow screens the line wraps, and remaining
      // spots is the actionable bit — the coach already knows their rate
      const stillFillable = ['confirmed', 'invited'].includes(this.lessonSlot.status);
      if (plan.max_wrestler_count > 1 && stillFillable) {
        const openSpots = Math.max(0, plan.max_wrestler_count - this.lessonSlot.confirmedCount);
        parts.push(`${openSpots} of ${plan.max_wrestler_count} spots open`);
      }
      parts.push(`${this.formatAsCurrency(plan.amount, 0, 2)}/hr per wrestler`);
      return parts.join(' · ');
    },
    // Booked/invited wrestlers, worth surfacing under the time. Capacity lives
    // in planSummary now.
    wrestlerSummary() {
      const wrestlers = this.lessonSlot.wrestlers || [];
      if (wrestlers.length === 0) {
        return null;
      }
      return wrestlers.map((w) => {
        const suffix = w.status !== 'confirmed' && w.status !== 'completed' ? ` (${this.wrestlerStatusLabel(w.status).toLowerCase()})` : '';
        return `${w.name}${suffix}`;
      }).join(', ');
    },
    openSummary() {
      const parts = ['Open slot'];
      if (this.lessonSlot.location) {
        // Structured locations (locationId set) serialize as
        // "Venue Name — street, city, state zip" — the venue name is enough
        // here, the full address lives on the event page. Free-text locations
        // are shown verbatim: a coach may have typed " — " meaningfully.
        parts.push(this.lessonSlot.locationId
          ? this.lessonSlot.location.split(' — ')[0].trim()
          : this.lessonSlot.location);
      }
      if (this.lessonSlot.rosterNames && this.lessonSlot.rosterNames.length > 0) {
        parts.push(`${this.lessonSlot.rosterNames.join(', ')} only`);
      }
      return parts.join(' · ');
    },
    // Only open slots are deletable here; anything a wrestler holds goes through
    // the event page where cancellation notifies the family. The coach booking
    // stays pending until a wrestler confirms, so also require no live
    // wrestler bookings — matching the server-side bulk-close rule.
    deletable() {
      return this.lessonSlot.status === 'pending' && (this.lessonSlot.wrestlers || []).length === 0;
    },
    confirmingDelete() {
      return this.confirmingDeleteSlotId === this.lessonSlot.id;
    },
    deleteFormEvent() {
      return {
        id: this.lessonSlot.id,
        name: `${this.timeRange} availability`,
      };
    },
    isPast() {
      return this.lessonSlot.endAt.isBefore(moment());
    },
    actionType() {
      if (this.lessonSlot.status === 'invited') {
        return 'confirm';
      }
      if (this.lessonSlot.status === 'confirmed' && this.isPast && this.lessonSlot.privateLessonId) {
        return 'checkout';
      }
      if (this.lessonSlot.status === 'pending' && !this.isPast) {
        return 'book';
      }
      return null;
    },
  },
  methods: {
    viewSlot() {
      if (this.forAppEmbed) {
        this.askAppToPush(`${window.location.origin}/app_embeds/events/${this.lessonSlot.id}?use_native=false`, 'View Lesson');
        return;
      }
      window.location.assign(`/events/${this.lessonSlot.id}`);
    },
  },
};
</script>
